import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { sessionActions } from '../../../../store';
import { EVV_GEOLOCATION_ERRORS } from '../../constants';
import { buildEvvLocationPayload, getEvvGeolocation } from '../../utils';

import type {
  EvvGpsCaptureStatus,
  UseEvvGpsCaptureParams,
  UseEvvGpsCaptureResult,
} from './types';

export function useEvvGpsCapture({
  token,
  onCaptured,
}: UseEvvGpsCaptureParams): UseEvvGpsCaptureResult {
  const dispatch = useDispatch();
  const [captureStatus, setCaptureStatus] = useState<EvvGpsCaptureStatus>('ready');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const setCaptureError = useCallback((message: string) => {
    setCaptureStatus('error');
    setErrorMessage(message);
    toast.error(message);
  }, []);

  const captureGps = useCallback(async () => {
    if (!token) {
      setCaptureError('EVV verification link is missing a token');
      return;
    }

    setCaptureStatus('capturing');
    setErrorMessage('');
    const location = await getEvvGeolocation();
    if ('error' in location) {
      setCaptureError(
        location.error === EVV_GEOLOCATION_ERRORS.UNSUPPORTED
          ? 'Location services are not available in this browser'
          : 'Unable to capture your location. Please allow location access and try again',
      );
      return;
    }

    const response = await dispatch(
      sessionActions.captureEvvGps({
        token,
        data: buildEvvLocationPayload(location.coords),
      }),
    );

    if (response.error) {
      setCaptureError(response.error.message);
      return;
    }

    setCaptureStatus('done');
    onCaptured?.();
  }, [dispatch, onCaptured, setCaptureError, token]);

  return {
    captureStatus,
    errorMessage,
    captureGps,
  };
}
